/**
 * The "Vilken princip?" set (pedagogy spec §7.3.5, E5) at `/practice/principles`: the statement,
 * `given` and figure, five principle chips, the right principle with its cue, and on to the next.
 * No calculation, no hints, no formula panel, no solution, no answer area. A wrapper in the
 * manner of `today/TodayPage.tsx`: the practice session page is not involved, so none of its
 * answer shortcuts can reach this page.
 *
 * Keyboard (through `shortcutsBlocked`): N moves on once the pick is made. Enter, S and H do
 * nothing here; the chips are buttons and take Enter and Space themselves. Each pick is posted at
 * once (`POST /api/practice/plan`), so leaving half-way keeps the evidence. A pick that could not
 * be saved is kept with its feedback (the student has seen the answer, so a second, informed pick
 * would store a correct row): it is marked unsaved, "Försök igen" sends the same pick and N waits
 * until the server has it.
 */
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { useLocalizedText } from "@client/components/Localized";
import { Button, Card, Kbd, ProgressBar, useToast } from "@client/components/ui";
import { useT, useTp } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { shortcutsBlocked } from "@client/lib/keyboard";
import { PlanStep } from "../PlanStep";
import { QuestionCard } from "../QuestionCard";
import { usePostPlan, type PostPlanBody } from "./api";
import {
  clearPlanSet,
  loadPlanSet,
  planSetResult,
  savePlanSet,
  withSavedPick,
  withUnsavedPick,
  type PlanSetData,
} from "./planSet";

export function WhichPrinciplePage() {
  const t = useT();
  const tp = useTp();
  const toast = useToast();
  const [data, setData] = useState<PlanSetData | null>(() => loadPlanSet());
  const post = usePostPlan();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const previousIndex = useRef(data?.index);

  useEffect(() => {
    if (data) savePlanSet(data);
  }, [data]);

  // "Nästa" unmounts the problem and its feedback: move focus to the heading, not to <body>.
  const index = data?.index;
  useEffect(() => {
    if (previousIndex.current !== index) h1Ref.current?.focus();
    previousIndex.current = index;
  }, [index]);

  const current =
    data && data.index < data.questions.length ? data.questions[data.index] : undefined;
  const picked = current !== undefined && data?.picks[current.id] !== undefined;

  function next() {
    setData((prev) => (prev ? { ...prev, index: prev.index + 1 } : prev));
  }

  /** Posts a pick; the same call serves the first send and "Försök igen" (a repeat row is harmless). */
  async function send(questionId: string, topicId: string) {
    const body: PostPlanBody = {
      questionId,
      topicId,
      origin: {
        kind: "exam-wrapper",
        set: "which-principle",
        ...(data?.from ? { examSessionId: data.from } : {}),
      },
    };
    try {
      await post.mutateAsync(body);
      setData((prev) => (prev ? withSavedPick(prev, questionId) : prev));
    } catch (error) {
      // The feedback was shown at once and the answer is known, so the pick is not taken back for
      // a second, informed try: it stays unsaved until the same pick is sent again.
      toast.error(messageForError(error, t));
    }
  }

  async function pick(topicId: string) {
    if (!data || !current || data.picks[current.id] !== undefined) return;
    const questionId = current.id;
    setData((prev) => (prev ? withUnsavedPick(prev, questionId, topicId) : prev));
    await send(questionId, topicId);
  }

  function retry() {
    const topicId = current ? data?.picks[current.id] : undefined;
    if (current && topicId !== undefined) void send(current.id, topicId);
  }

  // Unsaved from the pick until the server has it: a page that loads one (after a reload) offers
  // the retry at once, since the post it sent may never have arrived.
  const unsaved = current !== undefined && data?.unsaved?.[current.id] === true;
  const canNext = picked && !unsaved && !post.isPending;

  // Refreshed every render so the (subscribe-once) keydown listener calls the latest closure.
  const latest = useRef({ next, canNext });
  latest.current = { next, canNext };

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (shortcutsBlocked(event) || event.repeat) return;
      if ((event.key === "n" || event.key === "N") && latest.current.canNext) {
        latest.current.next();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  if (!data) return <Navigate to="/practice" replace />;
  if (data.index >= data.questions.length) {
    return (
      <div className="mx-auto max-w-2xl">
        <PlanSetSummary data={data} />
      </div>
    );
  }
  if (!current) return null;

  const total = data.questions.length;
  const offer = data.plans[current.id];
  const short = data.questions.length < data.requested;
  return (
    <div className="mx-auto max-w-2xl">
      <h1
        ref={h1Ref}
        tabIndex={-1}
        className="font-display text-2xl text-ink-900 outline-none dark:text-ink-50"
      >
        {t("practice.whichPrinciple.title")}
      </h1>
      <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">
        {t("practice.whichPrinciple.intro")}
      </p>
      {short && (
        <p role="status" className="mt-2 text-sm text-ink-700 dark:text-ink-200">
          {tp("practice.whichPrinciple.short", total, { count: total })}
        </p>
      )}
      <ProgressBar
        value={data.index / total}
        label={t("practice.session.progress")}
        caption={`${data.index + 1}/${total}`}
        className="my-4"
      />
      <QuestionCard question={current} />
      {offer && (
        <PlanStep
          key={current.id}
          offer={offer}
          pick={data.picks[current.id]}
          onPick={(topicId) => void pick(topicId)}
          note={t("practice.whichPrinciple.note")}
        />
      )}
      {picked && (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
          {unsaved && !post.isPending ? (
            <>
              <p id="plan-set-unsaved" className="text-sm text-ink-700 dark:text-ink-200">
                {t("practice.whichPrinciple.unsaved")}
              </p>
              <Button variant="secondary" onClick={retry} aria-describedby="plan-set-unsaved">
                {t("common.retry")}
              </Button>
            </>
          ) : (
            <Button
              onClick={next}
              disabled={!canNext}
              iconRight={<ArrowRight size={16} aria-hidden="true" />}
            >
              {t("common.next")} <Kbd className="ml-1">N</Kbd>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** "{right} av {total} principer rätt" and the principles to practise, each with its cue. */
function PlanSetSummary({ data }: { data: PlanSetData }) {
  const t = useT();
  const navigate = useNavigate();
  const localize = useLocalizedText();
  const { total, right, missed } = planSetResult(data);
  const headingRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <Card
      title={
        <span ref={headingRef} tabIndex={-1} className="outline-none">
          {t("practice.whichPrinciple.title")}
        </span>
      }
    >
      <p className="text-xl font-display text-ink-900 dark:text-ink-50">
        {t("practice.whichPrinciple.summary", { right, total })}
      </p>
      {missed.length > 0 && (
        <section aria-labelledby="plan-set-missed" className="mt-4">
          <h3 id="plan-set-missed" className="text-sm font-semibold text-ink-900 dark:text-ink-50">
            {t("practice.whichPrinciple.missed")}
          </h3>
          <ul className="mt-2 space-y-2">
            {missed.map((principle) => (
              <li key={principle.topicId} className="text-sm">
                <span className="font-medium text-ink-900 first-letter:uppercase dark:text-ink-50">
                  {localize(principle.name)}
                </span>
                <span className="text-ink-700 dark:text-ink-300"> {localize(principle.cue)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="mt-6">
        <Button
          onClick={() => {
            clearPlanSet();
            navigate("/practice");
          }}
        >
          {t("practice.summary.newSession")}
        </Button>
      </div>
    </Card>
  );
}

export default WhichPrinciplePage;
